import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    TrendingUp,
    Users,
    ShieldCheck,
    Clock,
    ArrowRight,
    Briefcase,
    Home as HomeIcon,
    HardHat,
} from "lucide-react";
import HeroReveal from "../components/HeroReveal";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import FadeUp from "../components/FadeUp";
import ContactForm from "../components/ContactForm";
import { api } from "../lib/api";
import { SERVICE_CATEGORIES } from "../lib/services";

const WHY = [
    {
        icon: ShieldCheck,
        title: "Built for resale",
        body: "Every choice is weighed against return. Finishes, layouts, scope, and schedule.",
    },
    {
        icon: Clock,
        title: "Reliable scheduling",
        body: "Tight project plans with clear handoffs. Stakeholders know what's happening, and when.",
    },
    {
        icon: HardHat,
        title: "Technical precision",
        body: "Local expertise, careful execution, and quality control from foundation to finish.",
    },
    {
        icon: Users,
        title: "Strategic partnership",
        body: "We think like an operator, protecting timelines, budgets, and ROI on every project.",
    },
];

const AUDIENCES = [
    {
        icon: Briefcase,
        eyebrow: "For Realtors",
        title: "Win listings. Sell faster.",
        body: "Pre-listing renovation packages designed to lift offers, shorten days-on-market, and protect commission.",
        link: "/real-estate-partner",
    },
    {
        icon: TrendingUp,
        eyebrow: "For Investors",
        title: "Engineered for ROI.",
        body: "Scope-tight, timeline-driven flips and value-add renovations built around your spread and exit strategy.",
        link: "/real-estate-partner",
    },
    {
        icon: HomeIcon,
        eyebrow: "For Homeowners",
        title: "Elevate, then own it.",
        body: "Residential upgrades that improve daily living and quietly compound long-term home value.",
        link: "/residential-renovations",
    },
];

export default function Home() {
    const [pairs, setPairs] = useState([]);
    useEffect(() => {
        api.get("/before-after")
            .then((r) => setPairs(r.data || []))
            .catch(() => setPairs([]));
    }, []);

    return (
        <main data-testid="home-page" className="bg-[#050505]">
            {/* HERO */}
            <section className="relative min-h-[100svh] flex items-end pt-32 pb-20">
                <HeroReveal />

                <div className="container-bevel relative z-10 w-full">
                    <div className="max-w-3xl">
                        <FadeUp delay={1}>
                            <span className="overline">
                                Tulsa · Owasso · Oklahoma
                            </span>
                        </FadeUp>
                        <FadeUp
                            delay={2}
                            as="h1"
                            className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-6 leading-[0.95]"
                        >
                            Built for resale.
                            <br />
                            <span className="text-[#F5A623]">
                                Designed for ROI.
                            </span>
                        </FadeUp>
                        <FadeUp delay={3}>
                            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                                Bevel Commercial Services is a high-level
                                renovation partner. We manage commercial and
                                residential transformations with the precision,
                                pace, and cost discipline serious property
                                owners require.
                            </p>
                        </FadeUp>
                        <FadeUp delay={4}>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="hero-cta-quote"
                                    className="btn-primary"
                                >
                                    Get a Project Quote{" "}
                                    <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/commercial-projects"
                                    data-testid="hero-cta-work"
                                    className="btn-secondary"
                                >
                                    View Our Work
                                </Link>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Stat strip */}
                    <FadeUp delay={6}>
                        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                            {[
                                { v: "100%", l: "Project oversight" },
                                { v: "OK", l: "Tulsa-based crews" },
                                { v: "ROI", l: "Resale-driven scope" },
                                { v: "1:1", l: "Direct PM access" },
                            ].map((s) => (
                                <div
                                    key={s.l}
                                    className="bg-[#050505]/90 backdrop-blur px-6 py-6"
                                >
                                    <div className="font-display text-3xl text-white">
                                        {s.v}
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-2">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* SERVICES */}
            <section
                id="services"
                className="section-pad relative"
                data-testid="services-section"
            >
                <div className="container-bevel">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <FadeUp className="md:col-span-5">
                            <span className="overline">What We Do</span>
                            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                                A single partner.
                                <br />
                                Full-cycle execution.
                            </h2>
                        </FadeUp>
                        <FadeUp className="md:col-span-6 md:col-start-7" delay={1}>
                            <p className="text-white/60 leading-relaxed">
                                From ground-up construction to final punch
                                list, Bevel delivers the full stack of
                                commercial and residential building services,
                                managed under one roof, one schedule, one
                                accountable team.
                            </p>
                            <Link
                                to="/project-checklist"
                                data-testid="services-checklist-cta"
                                className="btn-primary mt-7"
                            >
                                Send a Detailed Scope
                                <ArrowRight size={14} />
                            </Link>
                        </FadeUp>
                    </div>

                    <div className="mt-14 space-y-px bg-white/10 border border-white/10">
                        {SERVICE_CATEGORIES.map((cat, ci) => (
                            <FadeUp
                                key={cat.id}
                                delay={ci}
                                className="bg-[#0A0A0A] p-8 md:p-10"
                                data-testid={`service-category-${cat.id}`}
                            >
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                                    <div className="lg:col-span-4">
                                        <cat.icon
                                            size={28}
                                            className="text-[#F5A623]"
                                            strokeWidth={1.5}
                                        />
                                        <span className="overline mt-4 block">
                                            {cat.eyebrow}
                                        </span>
                                        <h3 className="font-display text-2xl md:text-3xl text-white mt-3 leading-tight">
                                            {cat.title}
                                        </h3>
                                        <p className="text-white/55 text-sm mt-4 leading-relaxed">
                                            {cat.summary}
                                        </p>
                                    </div>
                                    <div
                                        className={`lg:col-span-8 grid gap-px bg-white/10 border border-white/10 ${
                                            cat.groups.length === 1
                                                ? "grid-cols-1"
                                                : cat.groups.length === 2
                                                  ? "grid-cols-1 md:grid-cols-2"
                                                  : "grid-cols-1 md:grid-cols-3"
                                        }`}
                                    >
                                        {cat.groups.map((g) => (
                                            <div
                                                key={g.title}
                                                className="bg-[#050505] p-6"
                                            >
                                                <div className="flex items-center gap-2 text-[#F5A623]">
                                                    <g.icon size={16} />
                                                    <span className="text-[11px] uppercase tracking-[0.2em]">
                                                        {g.title}
                                                    </span>
                                                </div>
                                                <ul
                                                    className={`mt-4 ${
                                                        cat.groups.length === 1
                                                            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2"
                                                            : "space-y-2"
                                                    }`}
                                                >
                                                    {g.items.map((it) => (
                                                        <li
                                                            key={it}
                                                            className="flex items-start gap-2 text-sm text-white/70"
                                                        >
                                                            <span className="w-1 h-1 bg-[#F5A623] mt-2 shrink-0" />
                                                            <span className="leading-relaxed">
                                                                {it}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* BEFORE/AFTER */}
            <section
                className="section-pad bg-[#080808] hairline-top"
                data-testid="before-after-section"
            >
                <div className="container-bevel">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <FadeUp>
                            <span className="overline">Transformations</span>
                            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
                                We don't just renovate.
                                <br />
                                We{" "}
                                <span className="text-[#F5A623]">
                                    transform property value.
                                </span>
                            </h2>
                        </FadeUp>
                        <FadeUp delay={1}>
                            <p className="text-white/60 max-w-md">
                                Drag the slider to compare. These are real
                                Bevel projects. Strategic upgrades that move
                                comps, not just cosmetics.
                            </p>
                        </FadeUp>
                    </div>

                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {pairs.length === 0 && (
                            <div
                                data-testid="before-after-loading"
                                className="col-span-full text-white/40 text-sm"
                            >
                                Loading transformations...
                            </div>
                        )}
                        {pairs.map((p, i) => (
                            <FadeUp key={p.id} delay={i}>
                                <BeforeAfterSlider
                                    beforeUrl={p.before_url}
                                    afterUrl={p.after_url}
                                    title={p.title}
                                    location={p.location}
                                    testId={`before-after-${i + 1}`}
                                />
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE BEVEL */}
            <section
                className="section-pad bg-[#050505]"
                data-testid="why-bevel-section"
            >
                <div className="container-bevel grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <FadeUp>
                            <span className="overline">Why Bevel</span>
                            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                                Hire a partner, not a contractor.
                            </h2>
                            <p className="text-white/60 mt-6 leading-relaxed">
                                Generic contractors finish jobs. Bevel finishes
                                projects that perform on schedule, on budget,
                                and built to lift property value.
                            </p>
                        </FadeUp>
                    </div>
                    <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {WHY.map((w, i) => (
                            <FadeUp
                                key={w.title}
                                delay={i}
                                className="bg-[#0A0A0A] p-8"
                            >
                                <w.icon
                                    size={24}
                                    className="text-[#F5A623]"
                                    strokeWidth={1.5}
                                />
                                <h3 className="font-display text-lg text-white mt-5">
                                    {w.title}
                                </h3>
                                <p className="text-white/60 text-sm mt-2 leading-relaxed">
                                    {w.body}
                                </p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* TARGET AUDIENCE */}
            <section
                className="section-pad bg-[#080808] hairline-top"
                data-testid="audience-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">Who We Work With</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-3xl">
                            Built for the people moving real estate forward.
                        </h2>
                    </FadeUp>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {AUDIENCES.map((a, i) => (
                            <FadeUp
                                key={a.title}
                                delay={i}
                                className="bg-[#0A0A0A] p-8 md:p-10 flex flex-col"
                            >
                                <a.icon
                                    size={28}
                                    className="text-[#F5A623]"
                                    strokeWidth={1.5}
                                />
                                <span className="overline mt-6">
                                    {a.eyebrow}
                                </span>
                                <h3 className="font-display text-2xl mt-3 text-white">
                                    {a.title}
                                </h3>
                                <p className="text-white/60 mt-3 leading-relaxed text-sm flex-1">
                                    {a.body}
                                </p>
                                <Link
                                    to={a.link}
                                    className="mt-8 text-[#F5A623] text-sm uppercase tracking-[0.2em] hover:underline inline-flex items-center gap-2"
                                    data-testid={`audience-link-${i}`}
                                >
                                    Learn more <ArrowRight size={14} />
                                </Link>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad bg-[#050505]">
                <div className="container-bevel">
                    <FadeUp>
                        <div className="border border-white/10 bg-gradient-to-br from-[#0F0F0F] to-[#050505] p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-7">
                                <span className="overline">Start a Project</span>
                                <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">
                                    Your partner in profitable property
                                    transformation.
                                </h2>
                                <p className="text-white/60 mt-5 max-w-xl">
                                    Tell us about the property. We'll respond
                                    within 24 hours with a free walkthrough or
                                    consult.
                                </p>
                            </div>
                            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="cta-section-quote"
                                    className="btn-primary justify-center"
                                >
                                    Get a Project Quote{" "}
                                    <ArrowRight size={16} />
                                </Link>
                                <a
                                    href="tel:+19183003797"
                                    data-testid="cta-section-call"
                                    className="btn-secondary justify-center"
                                >
                                    Call (918) 300-3797
                                </a>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Inline contact (anchor for /contact CTA on home) */}
            <section
                className="section-pad bg-[#080808] hairline-top"
                id="quick-contact"
                data-testid="home-quick-contact"
            >
                <div className="container-bevel grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <FadeUp>
                            <span className="overline">Project Inquiry</span>
                            <h2 className="font-display text-4xl mt-4 leading-tight">
                                Share your project details.
                            </h2>
                            <p className="text-white/60 mt-5">
                                Bevel Commercial Services bridges the gap
                                between architectural vision and structural
                                reality, with the technical precision your
                                project demands.
                            </p>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-7">
                        <FadeUp delay={1}>
                            <ContactForm />
                        </FadeUp>
                    </div>
                </div>
            </section>
        </main>
    );
}
